import "./ProjectAccessRollupTable.css";

import { ArrowRoundLeftDarkIcon } from "../../../assets";
import { ResultData } from "../../../service/Shared";
import {
  DataGrid,
  DataGridCellFormatter,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridSelectCellFormatterData,
} from "../../data";
import { Button, KanaTooltip } from "../../forms";
import { LoadingSpinner } from "../../general";
import { Card, Column, Row } from "../../layout";
import { TextLink } from "../../navigation";
import { OrganisationsCell, OrganisationsCellData } from "./components";
import { ProjectAccessRollupTableProps } from "./models";

// Each organisation carries the control that takes it back off the project, so the cell is a
// component rather than the string the stringWithEllipsis formatter joined.
const OrganisationsCellFormatter: DataGridCellFormatter<OrganisationsCellData> = () => {
  return (data) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <OrganisationsCell {...data} />
  );
};

// Project, Landholding and Responsible owner are fixed so the remaining width all falls to
// Organisations & access, which holds the longest text. Without a width on the others they would
// otherwise take an equal share of the free space and leave that column cramped.
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<OrganisationsCellData>)[] = [
  { key: "projectName", name: "Project", dataType: "string", minWidth: 220, width: 260 },
  {
    key: "landholdingName",
    name: "Landholding",
    dataType: "string",
    sortable: false,
    filterable: false,
    minWidth: 180,
    width: 220,
  },
  {
    key: "organisations",
    name: "Organisations & access",
    dataType: "string",
    formatter: "custom",
    customCellFormatter: OrganisationsCellFormatter,
    sortable: false,
    filterable: false,
    // Only a floor, not a target: this column has no width, so it already absorbs everything the
    // other three leave. react-data-grid honours a minWidth even past the grid's own width, so the
    // four floors added together are what decides whether a horizontal scrollbar appears - and
    // this column ellipsises, so a wide floor would buy nothing but that scrollbar.
    minWidth: 220,
  },
  {
    key: "responsibleOwner",
    name: "Responsible owner",
    dataType: "string",
    formatter: "select",
    sortable: false,
    filterable: false,
    minWidth: 280,
    width: 320,
    // Same wording as AssignResponsibleOwnerModal, so the explanation and the confirmation agree.
    tooltip: (
      <KanaTooltip
        tooltipHeader="What is a responsible owner?"
        tooltipText="Only one organisation can hold this role at a time. The responsible owner manages the project and its access. Assigning it to another organisation removes it from the one that currently holds it."
      />
    ),
  },
];

export const ProjectAccessRollupTable = ({
  rows,
  addOrganisation,
  responsibleOwnerOptions,
  onResponsibleOwnerChange,
  onRemoveOrganisation,
  onPrevious,
  isPreviousDisabled = false,
  onSave,
  onLearnMore,
  isLoading = false,
  loadingMessage = "Creating your projects and sending invitations. This can take a moment for many projects.",
  corners = "default",
  variant = "default",
}: ProjectAccessRollupTableProps): JSX.Element => {
  const data: ResultData[] = rows.map((row) => ({
    uuid: row.projectKey,
    projectName: row.projectName,
    landholdingName: row.landholdingName,
    organisations: {
      projectKey: row.projectKey,
      organisations: row.organisations,
      onRemove: onRemoveOrganisation,
    } as OrganisationsCellData,
    responsibleOwner: {
      data: responsibleOwnerOptions(row.projectKey),
      value: row.responsibleOwnerOrganisationUuid,
      placeholder: "Select",
      size: "small",
      corners,
      // Reads as part of the row rather than a form control, so it drops the box and its padding
      // and sits flush with the column header.
      hasBorder: false,
      onChange: (organisationUuid: string) => onResponsibleOwnerChange(row.projectKey, organisationUuid),
    } as DataGridSelectCellFormatterData,
  }));

  return (
    <Card corners={corners} padding="ll">
      <div className="ProjectAccessRollupTable">
        <Row spacingV="m">
          <Column span={12}>
            <h2>Organisations &amp; access</h2>
          </Column>
        </Row>

        <Row spacingV="ll">
          <Column span={12}>
            <p className="body1">
              Invite an organisation, set its access level, and choose which projects it applies to. Add as many
              organisations as you need; each project can have any number. Once invited, the organisation is notified,
              and you&apos;ll be alerted when they respond.{" "}
              {onLearnMore && (
                <TextLink onAction={onLearnMore}>Learn about organisation types and permissions</TextLink>
              )}
            </p>
          </Column>
        </Row>

        {addOrganisation && (
          <Row spacingV="ll">
            <Column span={12}>{addOrganisation}</Column>
          </Row>
        )}

        {/* The Save button's own spinner sits below a table that can run to many rows, so the
            wait is also said in words, just above the grid. */}
        {isLoading && (
          <Row spacingV="ml">
            <Column span={12}>
              <div className="ProjectAccessRollupTable_loading" role="status" aria-live="polite">
                <LoadingSpinner type="blue" size="medium" />
                <span className="body1">{loadingMessage}</span>
              </div>
            </Column>
          </Row>
        )}

        <Row spacingV="4xl">
          <Column span={12}>
            <DataGrid
              columns={columns}
              data={data}
              clientSide
              filterable={false}
              pageable={false}
              // useDataGrid ignores `pageable` and still slices client-side, so the page has to hold every row.
              pagination={{ pageSize: Math.max(rows.length, 1) }}
              noDataMessage="No projects to display"
              dataIsLoading={isLoading}
              variant={variant}
            />
          </Column>
        </Row>

        <Row>
          <Column span={12}>
            <div className="ProjectAccessRollupTable_actions">
              <Button
                variant="secondary"
                text="Previous"
                icon={<ArrowRoundLeftDarkIcon />}
                iconPosition="left"
                corners={corners}
                disabled={isLoading || isPreviousDisabled}
                onClick={onPrevious}
              />
              <Button text="Save" corners={corners} isLoading={isLoading} onClick={onSave} />
            </div>
          </Column>
        </Row>
      </div>
    </Card>
  );
};
